// L'onglet « Usage » de l'administration (issue #193).
//
// PostHog collecte, l'administration affiche : tout vient de
// `/api/admin/usage`, qui interroge PostHog côté serveur (src/admin/usage.ts)
// et garde la réponse cinq minutes. Les tableaux de PostHog ne sont plus
// entretenus ; ce qu'on veut voir s'ajoute ici.
//
// Trois origines, toujours dans le même ordre et la même couleur : extension
// (`--brand`), Radar (`--teal-ink`), site (`--ink-3`). Aucune couleur nouvelle.

import { useEffect, useRef, useState } from 'react'
import { get } from '../lib/api'
import { dateHeure, relTime } from '../lib/format'
import { Empty, Loading, useData } from '../components/common'

type Source = 'extension' | 'radar' | 'site'

interface UsageGlobal {
  jours: number
  genereLe: string
  parOrigine: Record<Source, number>
  personnes: { id: string; source: string; n: number; premier: string; dernier: string }[]
  annuaire: Annuaire
  actifs: ({ jour: string } & Record<Source, number>)[]
  evenements: { evenement: string; source: string; n: number; personnes: number; avant: number; dernier: string }[]
  quotidien: Record<string, Record<string, number>>
  entonnoirExtension: { etape: string; personnes: number }[]
  entonnoirRadar: { etape: string; personnes: number }[]
  retention: { semaine: string; source: string; taille: number; revenus: number[] }[]
  pays: { pays: string; personnes: number }[]
  erreurs: { evenement: string; detail: string; n: number; personnes: number; dernier: string }[]
  sansSuite: { id: string; quand: string; pays: string; version: string }[]
}

/** Identifiant PostHog vers adresse, pour les comptes Radar (issue #193). */
type Annuaire = Record<string, string>

interface Chronologie {
  id: string
  annuaire: Annuaire
  evenements: { quand: string; evenement: string; source: string; id: string; proprietes: Record<string, unknown> }[]
}

interface DetailEvenement {
  annuaire: Annuaire
  evenement: string
  proprietes: { cle: string; n: number }[]
  repartition: { cle: string | null; valeurs: { valeur: string; n: number }[] }
  derniers: { quand: string; id: string; proprietes: Record<string, unknown> }[]
}

interface Reponse<T> {
  configure: boolean
  donnees?: T
  erreur?: string
}

const SOURCES: { s: Source; nom: string; couleur: string }[] = [
  { s: 'extension', nom: 'Extension', couleur: 'var(--brand)' },
  { s: 'radar', nom: 'Radar', couleur: 'var(--teal-ink)' },
  { s: 'site', nom: 'Site', couleur: 'var(--ink-3)' },
]

/** Le nom lisible des events qu'on regarde souvent ; les autres gardent leur nom technique. */
const NOMS: Record<string, string> = {
  app_installed: 'Extension installée',
  app_updated: 'Extension mise à jour',
  app_opened: 'Extension ouverte',
  onboarding_completed: 'Prise en main terminée',
  cv_imported: 'CV importé (extension)',
  score_run: 'Score calculé par l’IA',
  score_cache_hit: 'Score relu du cache',
  cv_generated: 'CV adapté (extension)',
  cover_letter_generated: 'Lettre écrite (extension)',
  preview_opened: 'Aperçu ouvert',
  export_pdf: 'Export PDF',
  export_word: 'Export Word',
  extraction_failed: 'Offre illisible',
  ai_error: 'Erreur IA',
  js_error: 'Erreur JavaScript (extension)',
  export_failed: 'Export raté (extension)',
  app_uninstalled: 'Extension désinstallée',
  feedback_sent: 'Avis envoyé (extension)',
  radar_connected: 'Extension reliée à Radar',
  radar_disconnected: 'Extension déliée de Radar',
  radar_offer_saved: 'Offre envoyée à Radar',
  radar_profile_saved: 'CV enregistré dans Radar',
  radar_ecran: 'Écran ouvert',
  radar_erreur_js: 'Erreur JavaScript (Radar)',
  radar_erreur_serveur: 'Erreur serveur (Radar)',
  radar_clic: 'Clic vers le Chrome Web Store',
  radar_extension_zip: 'Zip de l’extension téléchargé',
  radar_extension_reliee: 'Extension reliée (côté Radar)',
  radar_code_extension: 'Code d’extension demandé',
  radar_offre_apportee: 'Offre apportée par l’extension',
  radar_cv_depuis_extension: 'CV retouché depuis l’extension',
  radar_dossier_apporte: 'CV adapté apporté par l’extension',
  radar_adaptation_extension: 'Adaptation autorisée (extension)',
  radar_abonnement: 'Abonnement (changement de statut)',
  radar_portail_ouvert: 'Portail d’abonnement ouvert',
  radar_salaire_demande: 'Salaire recherché',
  radar_entretien_prepare: 'Entretien préparé',
  radar_relance_redigee: 'Relance rédigée',
  radar_lettre_retouchee: 'Lettre retouchée',
  radar_cv_retouche_ia: 'CV retouché par l’IA',
  radar_cv_enregistre: 'CV adapté enregistré',
  radar_cv_retouche: 'CV maître retouché',
  radar_export: 'Export (PDF ou Word)',
  radar_offre_ajoutee: 'Offre ajoutée à la main',
  radar_site_essaye: 'Site essayé',
  radar_site_demande: 'Site demandé',
  radar_site_ajoute: 'Site ajouté',
  radar_employeurs_choisis: 'Employeurs choisis (inscription)',
  radar_metiers_corriges: 'Métiers corrigés',
  radar_exigences_lues: 'Exigences de l’offre lues',
  radar_avis_score: 'Avis sur un score',
  radar_reglages: 'Rythme des mails réglé',
  radar_reglages_cv: 'Réglages des CV adaptés',
  radar_retour_envoye: 'Retour envoyé',
  radar_mot_de_passe_oublie: 'Mot de passe oublié',
  radar_lien_oubli_ouvert: 'Lien de mot de passe ouvert',
  radar_mot_de_passe_change: 'Mot de passe changé',
  radar_langue_changee: 'Langue changée',
  radar_compte_supprime: 'Compte supprimé',
  radar_donnees_telechargees: 'Données téléchargées',
  radar_candidature_ouverte: 'Candidature ouverte',
  radar_candidature_archivee: 'Candidature archivée',
  radar_offres_triees_en_lot: 'Offres triées en lot',
  radar_questions_ecart: 'Questions d’écart posées',
  radar_lettre_angles: 'Angles de lettre proposés',
  radar_lettre_gardee: 'Lettre gardée après un nouveau CV',
  radar_lettre_version_restauree: 'Ancienne lettre reprise',
  radar_cv_version_restauree: 'Ancien CV repris',
  radar_mise_en_page: 'Mise en page changée',
  radar_faits_ajoutes: 'Précisions ajoutées au CV',
  radar_adresse_mails: 'Adresse des mails changée',
  radar_manque_ajoute: '« Je l’ai » sur un manque',
  radar_pont_refuse: 'Suggestion de suivi refusée',
  radar_suggestion: 'Suggestion de réglage',
  radar_connexion: 'Connexion',
  radar_inscription_demandee: 'Inscription demandée',
  radar_compte_cree: 'Compte créé',
  radar_bienvenue_terminee: 'Inscription terminée',
  radar_cv_importe: 'CV déposé',
  radar_offre_triee: 'Offre triée',
  radar_cv_adapte: 'CV adapté',
  radar_lettre_ecrite: 'Lettre écrite',
  radar_candidature: 'Candidature',
  radar_employeur_suivi: 'Employeur suivi ou quitté',
  radar_mail_ouvert: 'Lien de mail ouvert',
  radar_refus: 'Refus (quota, abonnement)',
  radar_caisse_ouverte: 'Caisse ouverte',
  site_vue: 'Page vue',
  site_clic: 'Clic',
}

const nomDe = (e: string) => NOMS[e] ?? e

/** Qui est derrière un identifiant : l'adresse d'un compte Radar, sinon ce
 *  qu'on en sait (une visite, une extension non reliée). */
function qui(id: string, annuaire: Annuaire, sansSuite?: Set<string>): { texte: string; connu: boolean } {
  const email = annuaire[id]
  if (email) return { texte: email, connu: true }
  if (id.startsWith('visite-')) return { texte: `visite ${id.slice(7, 13)}`, connu: false }
  if (id.startsWith('radar-')) return { texte: `compte ${id.slice(6, 12)} (supprimé ?)`, connu: false }
  return { texte: `anonyme ${id.slice(0, 6)}${sansSuite?.has(id) ? ', sans suite' : ''}`, connu: false }
}

function Qui({ id, annuaire, sansSuite }: { id: string; annuaire: Annuaire; sansSuite?: Set<string> }) {
  const q = qui(id, annuaire, sansSuite)
  return q.connu ? <b>{q.texte}</b> : <span className="muted" title={id}>{q.texte}</span>
}
const jourCourt = (j: string) => new Date(`${j}T12:00:00`).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })

/** Les jours de la période, du plus ancien à aujourd'hui, pour que les trous se voient. */
function joursDeLaPeriode(n: number): string[] {
  return [...Array(n)].map((_, i) => new Date(Date.now() - (n - 1 - i) * 86_400_000).toISOString().slice(0, 10))
}

export function Usage() {
  const [jours, setJours] = useState(30)
  const etat = useData(() => get<Reponse<UsageGlobal>>(`/api/admin/usage?jours=${jours}`), [jours])
  return (
    <>
      <div className="adm-periode" role="group" aria-label="Période">
        {[7, 30, 90].map((j) => (
          <button key={j} type="button" className={`chip${j === jours ? ' on' : ''}`} aria-pressed={j === jours} onClick={() => setJours(j)}>
            {j} jours
          </button>
        ))}
        {etat.data?.donnees && (
          <span className="sub" title={dateHeure(etat.data.donnees.genereLe)}>
            Lu dans PostHog {relTime(etat.data.donnees.genereLe)}, relu toutes les 5 minutes.
          </span>
        )}
      </div>
      {etat.error && <div className="avertissement">{etat.error}</div>}
      {!etat.data && !etat.error && <Loading />}
      {etat.data && !etat.data.configure && (
        <div className="avertissement">
          PostHog n’est pas relié : poser <code>POSTHOG_PERSONAL_KEY</code> (clé personnelle, droit <code>query:read</code>) dans{' '}
          <code>apps/console/.env</code>, puis redémarrer <code>admin-ui</code>.
        </div>
      )}
      {etat.data?.erreur && <div className="avertissement">{etat.data.erreur}</div>}
      {etat.data?.donnees && <Tableau u={etat.data.donnees} />}
    </>
  )
}

function Tableau({ u }: { u: UsageGlobal }) {
  const [choisi, setChoisi] = useState<string | null>(null)
  const [personne, setPersonne] = useState<string | null>(null)
  const total = (noms: string[]) => u.evenements.filter((e) => noms.includes(e.evenement)).reduce((s, e) => s + e.n, 0)
  const tuiles = [
    { v: u.parOrigine.extension, l: 'personnes dans l’extension' },
    { v: u.parOrigine.radar, l: 'personnes dans Radar' },
    { v: u.parOrigine.site, l: 'visites du site' },
    { v: total(['app_installed']) - u.sansSuite.length, l: 'installations utilisées' },
    { v: total(['cv_generated', 'radar_cv_adapte']), l: 'CV adaptés' },
  ]
  return (
    <>
      <div className="cstats adm-tuiles">
        {tuiles.map((t) => (
          <div className="ctile" key={t.l} style={{ '--h': 220 } as React.CSSProperties}>
            <div className="cv">{t.v}</div>
            <div className="cl">{t.l}</div>
          </div>
        ))}
      </div>

      <p className="sub">Compté depuis le 9 octobre 2026, jour de la publication sur le Chrome Web Store.</p>

      <section className="panel">
        <h4>Personnes actives par jour</h4>
        <CourbeActifs u={u} />
      </section>

      <div className="adm-grille adm-deux-egal">
        <section className="panel">
          <h4>Extension : jusqu’où vont ceux qui l’installent</h4>
          <p className="sub">Parmi les installations de la période.</p>
          <Entonnoir etapes={u.entonnoirExtension} couleur="var(--brand)" />
        </section>
        <section className="panel">
          <h4>Radar : de l’inscription au premier CV adapté</h4>
          <p className="sub">Par visite, puis par compte.</p>
          <Entonnoir etapes={u.entonnoirRadar} couleur="var(--teal-ink)" />
        </section>
      </div>

      <div className="adm-grille adm-deux">
        <section className="panel">
          <h4>Qui revient, semaine après semaine</h4>
          <p className="sub">Chaque ligne : les nouveaux d’une semaine, et la part revenue ensuite. Huit semaines.</p>
          <Retention lignes={u.retention} />
        </section>
        <section className="panel">
          <h4>Pays</h4>
          {u.pays.length === 0 ? (
            <Empty>Rien sur la période.</Empty>
          ) : (
            <ul className="adm-liste">
              {u.pays.map((p) => (
                <li key={p.pays}>
                  <span>{p.pays === '?' ? 'inconnu' : p.pays}</span>
                  <b className="mono">{p.personnes}</b>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {u.sansSuite.length > 0 && (
        <section className="panel">
          <h4>Installations sans suite : {u.sansSuite.length}</h4>
          <p className="sub">Installée, au plus ouverte, rien d’autre. Souvent la relecture de Google après une version.</p>
          <ul className="adm-liste">
            {u.sansSuite.map((x) => (
              <li key={x.id}>
                <span title={dateHeure(x.quand)}>{relTime(x.quand)}</span>
                <span className="mono muted">
                  {x.pays} · v{x.version}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h4>Erreurs</h4>
        <p className="sub">Extension et Radar. Chacune sonne aussi sur Telegram.</p>
        <TableErreurs u={u} />
      </section>

      <section className="panel">
        <h4>Personnes</h4>
        <p className="sub">Les comptes Radar par leur adresse, retrouvée ici : PostHog ne la reçoit jamais. Cliquer pour tout ce qu’une personne a fait.</p>
        <TablePersonnes u={u} choisie={personne} onChoisir={setPersonne} />
      </section>
      {personne && <VuePersonne id={personne} onFermer={() => setPersonne(null)} />}

      <section className="panel">
        <h4>Tous les événements</h4>
        <p className="sub">Comparés aux {u.jours} jours d’avant. Cliquer sur une ligne pour sa courbe et ses propriétés.</p>
        <TableEvenements u={u} choisi={choisi} onChoisir={setChoisi} />
      </section>
      {choisi && <Detail evenement={choisi} jours={u.jours} quotidien={u.quotidien[choisi] ?? {}} onFermer={() => setChoisi(null)} />}
    </>
  )
}

/** Une ligne par origine, un seul axe, la légende et un libellé en bout de ligne. */
function CourbeActifs({ u }: { u: UsageGlobal }) {
  const jours = joursDeLaPeriode(u.jours)
  const parJour = new Map(u.actifs.map((a) => [a.jour, a]))
  const valeur = (j: string, s: Source) => parJour.get(j)?.[s] ?? 0
  const max = Math.max(1, ...jours.flatMap((j) => SOURCES.map(({ s }) => valeur(j, s))))
  const L = 640
  const H = 170
  const G = 32
  const D = 80
  const x = (i: number) => G + (i / Math.max(jours.length - 1, 1)) * (L - G - D)
  const y = (n: number) => H - 22 - (n / max) * (H - 40)
  const presentes = SOURCES.filter(({ s }) => jours.some((j) => valeur(j, s) > 0))
  if (presentes.length === 0) return <Empty>Personne sur la période.</Empty>
  return (
    <figure className="adm-courbe">
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Personnes actives par jour et par origine">
        <line x1={G} x2={L - D} y1={y(0)} y2={y(0)} className="adm-axe" />
        <text x={G - 6} y={y(max) + 4} textAnchor="end" className="adm-lbl">{max}</text>
        <text x={G - 6} y={y(0) + 4} textAnchor="end" className="adm-lbl">0</text>
        <text x={G} y={H - 4} className="adm-lbl">{jourCourt(jours[0])}</text>
        <text x={L - D} y={H - 4} textAnchor="end" className="adm-lbl">aujourd’hui</text>
        {presentes.map(({ s, nom, couleur }) => (
          <g key={s}>
            <polyline
              points={jours.map((j, i) => `${x(i)},${y(valeur(j, s))}`).join(' ')}
              fill="none"
              stroke={couleur}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            <text x={L - D + 6} y={y(valeur(jours[jours.length - 1], s)) + 4} className="adm-lbl">
              {nom}
            </text>
          </g>
        ))}
        {/* Une colonne de survol par jour, sur toute la hauteur : le détail des trois origines. */}
        {jours.map((j, i) => (
          <rect key={j} x={x(i) - (L - G - D) / jours.length / 2} y={0} width={(L - G - D) / jours.length} height={H - 22} fill="transparent">
            <title>{`${jourCourt(j)} : ${SOURCES.map(({ s, nom }) => `${nom} ${valeur(j, s)}`).join(', ')}`}</title>
          </rect>
        ))}
      </svg>
      <figcaption className="adm-legende">
        {SOURCES.map(({ s, nom, couleur }) => (
          <span key={s}>
            <i style={{ background: couleur }} /> {nom}
          </span>
        ))}
      </figcaption>
    </figure>
  )
}

function Entonnoir({ etapes, couleur }: { etapes: { etape: string; personnes: number }[]; couleur: string }) {
  const base = etapes[0]?.personnes ?? 0
  const max = Math.max(1, ...etapes.map((e) => e.personnes))
  if (etapes.every((e) => e.personnes === 0)) return <Empty>Personne sur la période.</Empty>
  return (
    <ul className="adm-entonnoir">
      {etapes.map((e) => (
        <li key={e.etape}>
          <span className="adm-ent-nom">{e.etape}</span>
          <span className="adm-ent-barre" aria-hidden>
            <span style={{ width: `${(e.personnes / max) * 100}%`, background: couleur }} />
          </span>
          <b className="mono">{e.personnes}</b>
          <span className="mono muted">{base > 0 ? `${Math.round((e.personnes / base) * 100)} %` : ''}</span>
        </li>
      ))}
    </ul>
  )
}

function Retention({ lignes }: { lignes: UsageGlobal['retention'] }) {
  if (lignes.length === 0) return <Empty>Pas encore de cohorte.</Empty>
  const largeur = Math.max(1, ...lignes.map((l) => l.revenus.length))
  return (
    <div className="tblwrap">
      <table className="tbl adm-retention">
        <thead>
          <tr>
            <th>Semaine du</th>
            <th>Origine</th>
            <th>Nouveaux</th>
            {[...Array(largeur)].map((_, i) => (
              <th key={i}>S+{i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={`${l.semaine}${l.source}`}>
              <td>{jourCourt(l.semaine)}</td>
              <td>{SOURCES.find((s) => s.s === l.source)?.nom ?? l.source}</td>
              <td className="mono">{l.taille}</td>
              {[...Array(largeur)].map((_, i) => {
                const n = l.revenus[i]
                if (n === undefined) return <td key={i} />
                const part = l.taille ? n / l.taille : 0
                return (
                  <td key={i} className="mono" title={`${n} sur ${l.taille}`} style={{ background: `color-mix(in oklab, var(--brand) ${Math.round(part * 60)}%, transparent)` }}>
                    {Math.round(part * 100)} %
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableErreurs({ u }: { u: UsageGlobal }) {
  if (u.erreurs.length === 0) return <Empty>Aucune erreur sur la période.</Empty>
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Erreur</th>
            <th>Détail</th>
            <th>Nombre</th>
            <th>Personnes</th>
            <th>Dernière</th>
          </tr>
        </thead>
        <tbody>
          {u.erreurs.map((e) => (
            <tr key={`${e.evenement}|${e.detail}`}>
              <td>{nomDe(e.evenement)}</td>
              <td className="mono">{e.detail}</td>
              <td className="mono">{e.n}</td>
              <td className="mono">{e.personnes}</td>
              <td title={e.dernier ? dateHeure(e.dernier) : undefined}>{e.dernier ? relTime(e.dernier) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableEvenements({ u, choisi, onChoisir }: { u: UsageGlobal; choisi: string | null; onChoisir: (e: string) => void }) {
  if (u.evenements.length === 0) return <Empty>Aucun événement sur la période.</Empty>
  return (
    <div className="tblwrap">
      <table className="tbl adm-evenements">
        <thead>
          <tr>
            <th>Événement</th>
            <th>Origine</th>
            <th>Nombre</th>
            <th>Personnes</th>
            <th>Avant</th>
            <th>Dernier</th>
          </tr>
        </thead>
        <tbody>
          {u.evenements.map((e) => (
            <tr
              key={`${e.evenement}${e.source}`}
              className={e.evenement === choisi ? 'on' : undefined}
              onClick={() => onChoisir(e.evenement)}
              tabIndex={0}
              onKeyDown={(k) => k.key === 'Enter' && onChoisir(e.evenement)}
            >
              <td>
                {nomDe(e.evenement)} {NOMS[e.evenement] && <span className="mono muted">{e.evenement}</span>}
              </td>
              <td>{SOURCES.find((s) => s.s === e.source)?.nom ?? e.source}</td>
              <td className="mono">{e.n}</td>
              <td className="mono">{e.personnes}</td>
              <td className="mono muted">{e.avant}</td>
              <td title={e.dernier ? dateHeure(e.dernier) : undefined}>{e.dernier ? relTime(e.dernier) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Detail({ evenement, jours, quotidien, onFermer }: { evenement: string; jours: number; quotidien: Record<string, number>; onFermer: () => void }) {
  const [cle, setCle] = useState<string | null>(null)
  const etat = useData(
    () => get<Reponse<DetailEvenement>>(`/api/admin/usage/evenement?nom=${encodeURIComponent(evenement)}&jours=${jours}${cle ? `&cle=${encodeURIComponent(cle)}` : ''}`),
    [evenement, jours, cle]
  )
  const d = etat.data?.donnees
  // Le détail s'ouvre sous une longue table : on l'amène sous les yeux.
  const ici = useRef<HTMLElement>(null)
  useEffect(() => {
    ici.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [evenement])
  return (
    <section className="panel" ref={ici}>
      <div className="adm-detail-tete">
        <h4>
          {nomDe(evenement)} <span className="mono muted">{evenement}</span>
        </h4>
        <button type="button" className="btn btn-sm" onClick={onFermer}>
          Fermer
        </button>
      </div>
      <BarresEvenement quotidien={quotidien} jours={jours} nom={nomDe(evenement)} />
      {etat.error && <div className="avertissement">{etat.error}</div>}
      {etat.data?.erreur && <div className="avertissement">{etat.data.erreur}</div>}
      {!etat.data && !etat.error && <Loading />}
      {d && (
        <div className="adm-grille adm-deux">
          <div>
            {d.proprietes.length > 0 && (
              <div className="adm-periode" role="group" aria-label="Propriété">
                {d.proprietes.map((p) => (
                  <button
                    key={p.cle}
                    type="button"
                    className={`chip${p.cle === d.repartition.cle ? ' on' : ''}`}
                    aria-pressed={p.cle === d.repartition.cle}
                    onClick={() => setCle(p.cle)}
                  >
                    {p.cle}
                  </button>
                ))}
              </div>
            )}
            {d.repartition.cle ? (
              <Entonnoir etapes={d.repartition.valeurs.map((v) => ({ etape: v.valeur, personnes: v.n }))} couleur="var(--brand)" />
            ) : (
              <Empty>Cet événement ne porte aucune propriété.</Empty>
            )}
          </div>
          <div>
            <h4>Les derniers</h4>
            <ul className="adm-liste adm-evts">
              {d.derniers.map((x, i) => (
                <li key={i}>
                  <span title={dateHeure(x.quand)}>{relTime(x.quand)}</span>
                  <Qui id={x.id} annuaire={d.annuaire} />
                  <span className="adm-charge">
                    {Object.entries(x.proprietes)
                      .map(([k, v]) => `${k} : ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                      .join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  )
}

function TablePersonnes({ u, choisie, onChoisir }: { u: UsageGlobal; choisie: string | null; onChoisir: (id: string) => void }) {
  const [tous, setTous] = useState(false)
  // Les comptes connus d'abord : c'est eux qu'on cherche pour déboguer.
  const liste = [...u.personnes].sort((a, b) => Number(Boolean(u.annuaire[b.id])) - Number(Boolean(u.annuaire[a.id])))
  if (liste.length === 0) return <Empty>Personne sur la période.</Empty>
  const montres = tous ? liste : liste.slice(0, 25)
  const sansSuite = new Set(u.sansSuite.map((x) => x.id))
  return (
    <>
      <div className="tblwrap">
        <table className="tbl adm-evenements">
          <thead>
            <tr>
              <th>Qui</th>
              <th>Origine</th>
              <th>Événements</th>
              <th>Première fois</th>
              <th>Dernière fois</th>
            </tr>
          </thead>
          <tbody>
            {montres.map((p) => (
              <tr key={p.id} className={p.id === choisie ? 'on' : undefined} onClick={() => onChoisir(p.id)} tabIndex={0} onKeyDown={(k) => k.key === 'Enter' && onChoisir(p.id)}>
                <td>
                  <Qui id={p.id} annuaire={u.annuaire} sansSuite={sansSuite} />
                </td>
                <td>{SOURCES.find((s) => s.s === p.source)?.nom ?? p.source}</td>
                <td className="mono">{p.n}</td>
                <td title={dateHeure(p.premier)}>{relTime(p.premier)}</td>
                <td title={dateHeure(p.dernier)}>{relTime(p.dernier)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {liste.length > montres.length && (
        <button type="button" className="btn btn-sm" onClick={() => setTous(true)}>
          Voir les {liste.length}
        </button>
      )}
    </>
  )
}

function VuePersonne({ id, onFermer }: { id: string; onFermer: () => void }) {
  const etat = useData(() => get<Reponse<Chronologie>>(`/api/admin/usage/personne?id=${encodeURIComponent(id)}`), [id])
  const c = etat.data?.donnees
  const ici = useRef<HTMLElement>(null)
  useEffect(() => {
    ici.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [id])
  return (
    <section className="panel" ref={ici}>
      <div className="adm-detail-tete">
        <h4>{c ? <Qui id={id} annuaire={c.annuaire} /> : id}</h4>
        <button type="button" className="btn btn-sm" onClick={onFermer}>
          Fermer
        </button>
      </div>
      <p className="sub">90 derniers jours, la visite d’avant le compte comprise.</p>
      {etat.error && <div className="avertissement">{etat.error}</div>}
      {etat.data?.erreur && <div className="avertissement">{etat.data.erreur}</div>}
      {!etat.data && !etat.error && <Loading />}
      {c && c.evenements.length === 0 && <Empty>Rien sur 90 jours.</Empty>}
      {c && c.evenements.length > 0 && (
        <ul className="adm-liste adm-evts">
          {c.evenements.map((e, i) => (
            <li key={i}>
              <span title={dateHeure(e.quand)}>{dateHeure(e.quand)}</span>
              <b>{nomDe(e.evenement)}</b>
              <span className="muted">{SOURCES.find((s) => s.s === e.source)?.nom ?? e.source}</span>
              {e.id !== id && <span className="muted">avant le compte</span>}
              <span className="adm-charge">
                {Object.entries(e.proprietes)
                  .filter(([k]) => k !== 'source' && k !== 'visite')
                  .map(([k, v]) => `${k} : ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Une seule série : pas de légende, le titre la nomme. */
function BarresEvenement({ quotidien, jours, nom }: { quotidien: Record<string, number>; jours: number; nom: string }) {
  const liste = joursDeLaPeriode(jours).map((j) => ({ j, n: quotidien[j] ?? 0 }))
  const max = Math.max(0, ...liste.map((l) => l.n))
  if (max === 0) return <Empty>Aucun sur la période.</Empty>
  const L = 640
  const H = 140
  const G = 32
  const largeur = (L - G) / liste.length
  return (
    <figure className="adm-courbe">
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label={`${nom} par jour`}>
        <line x1={G} x2={L} y1={H - 18} y2={H - 18} className="adm-axe" />
        <text x={G - 6} y={14} textAnchor="end" className="adm-lbl">{max}</text>
        <text x={G - 6} y={H - 14} textAnchor="end" className="adm-lbl">0</text>
        <text x={G} y={H - 2} className="adm-lbl">{jourCourt(liste[0].j)}</text>
        <text x={L} y={H - 2} textAnchor="end" className="adm-lbl">aujourd’hui</text>
        {liste.map((l, i) => {
          const h = (l.n / max) * (H - 30)
          return (
            <g key={l.j}>
              <rect x={G + i * largeur} y={0} width={largeur} height={H - 18} fill="transparent">
                <title>{`${jourCourt(l.j)} : ${l.n}`}</title>
              </rect>
              {h > 0 && <rect x={G + i * largeur + 1} y={H - 18 - h} width={Math.max(largeur - 2, 2)} height={h} rx={2} fill="var(--brand)" pointerEvents="none" />}
            </g>
          )
        })}
      </svg>
    </figure>
  )
}
